import { FC } from 'react';

import {
  CouponPendingBrandSummary,
  CouponPendingSummaryResponse,
} from '@shared/api/couponSettlement/couponSettlement.interface';

import {
  amountColorClass,
  formatSettlementAmount,
} from '../../utils/coupon-settlement.utils';
import { ceilWon } from '../../utils/settlement.utils';

/** 경유(viaMari) 뱃지 — 미정산 통계 테이블과 상세 모달 헤더가 공유 */
export const viaMariBadge = (viaMari?: boolean) => {
  if (viaMari === undefined) return <span className="text-gray-300">-</span>;
  return (
    <span
      className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${
        viaMari ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {viaMari ? '마리' : '가온'}
    </span>
  );
};

export interface CouponPendingSummaryTableProps {
  brands: CouponPendingBrandSummary[];
  total?: CouponPendingSummaryResponse['total'];
  /** 상조 view(브랜드 1개)에서 상수 소음인 상조명·경유 칼럼 숨김 */
  hideBrand: boolean;
  onOpenDetail: (brandId: string) => void;
}

/**
 * 미정산 1단계 — 상조별 발송/환불 분리 집계 통계 테이블 (raw 미로드).
 * 발송 금액만 ceilWon — 서버 settle의 totalAmount = ceilWon(Σ라인) 기준과 맞춘 표기.
 */
export const CouponPendingSummaryTable: FC<CouponPendingSummaryTableProps> = ({
  brands,
  total,
  hideBrand,
  onOpenDetail,
}) => (
  <div className="overflow-x-auto rounded-lg bg-white shadow">
    <table className="min-w-full divide-y divide-gray-200 text-sm">
      <thead className="bg-gray-50 text-xs text-gray-500">
        <tr>
          {!hideBrand && <th className="px-3 py-2.5 text-left">상조</th>}
          {!hideBrand && <th className="px-3 py-2.5 text-left">경유</th>}
          <th className="px-3 py-2.5 text-right">발송 건수</th>
          <th className="px-3 py-2.5 text-right">발송 수익금(+)</th>
          <th className="px-3 py-2.5 text-right">환불 건수</th>
          <th className="px-3 py-2.5 text-right">환불 차감(−)</th>
          <th className="px-3 py-2.5 text-right">예상 정산금</th>
          <th className="px-3 py-2.5 text-center">상세</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {brands.length === 0 ? (
          <tr>
            <td
              colSpan={hideBrand ? 6 : 8}
              className="px-3 py-10 text-center text-gray-400"
            >
              미정산 내역이 없습니다.
            </td>
          </tr>
        ) : (
          brands.map((b) => (
            <tr key={b.bugoBrand._id} className="hover:bg-gray-50">
              {!hideBrand && (
                <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-700">
                  {b.bugoBrand.name}
                </td>
              )}
              {!hideBrand && (
                <td className="px-3 py-3">{viaMariBadge(b.bugoBrand.viaMari)}</td>
              )}
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                {b.send.count}건
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right font-medium tabular-nums text-emerald-700">
                +{ceilWon(b.send.totalAmount).toLocaleString('ko-KR')}
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                {b.refund.count}건
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-right font-medium tabular-nums text-rose-700">
                −{b.refund.totalAmount.toLocaleString('ko-KR')}
              </td>
              <td
                className={`whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums ${amountColorClass(
                  b.netAmount,
                )}`}
              >
                {formatSettlementAmount(b.netAmount)}
              </td>
              <td className="px-3 py-3 text-center">
                <button
                  onClick={() => onOpenDetail(b.bugoBrand._id)}
                  className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                >
                  상세보기
                </button>
              </td>
            </tr>
          ))
        )}
      </tbody>
      {/* 합계 행 — 브랜드 1개(상조 view)면 본문 행과 동일한 중복이라 생략 */}
      {total && brands.length > 0 && !hideBrand && (
        <tfoot className="bg-gray-50 text-sm font-semibold">
          <tr>
            <td className="px-3 py-3 text-gray-500" colSpan={2}>
              합계 ({brands.length}개 상조)
            </td>
            <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
              {total.sendCount}건
            </td>
            <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-emerald-700">
              +{ceilWon(total.sendAmount).toLocaleString('ko-KR')}
            </td>
            <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
              {total.refundCount}건
            </td>
            <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-rose-700">
              −{total.refundAmount.toLocaleString('ko-KR')}
            </td>
            <td
              className={`whitespace-nowrap px-3 py-3 text-right tabular-nums ${amountColorClass(
                total.netAmount,
              )}`}
            >
              {formatSettlementAmount(total.netAmount)}
            </td>
            <td />
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);
